// src/Components/Dashboard.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import './randi.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  timeout: 8000,
  validateStatus: status => status >= 200 && status < 300, // Reject non-2xx
});

const RandomRecipeComponent = () => {
  const [recipe, setRecipe] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userdata, setUserdata] = useState({});
  const [evaluation, setEvaluation] = useState(null);
  const [activeButton, setActiveButton] = useState(null);

  const mountedRef = useRef(true);

  /** Fetch authenticated user */
  const getUser = useCallback(async () => {
    if (!BACKEND_URL) {
      setError('Backend URL is not configured.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get('/ttc/login/userdata');
      if (!mountedRef.current) return;
      if (response.data?.user) {
        setUserdata(response.data.user);
        setError(null);
      } else {
        setError('User data not found. Please login again.');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('❌ Error fetching user:', err);
      setError('Unable to fetch user. Please login again.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  /** Fetch a random recipe */
  const fetchRandomRecipe = useCallback(async userId => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get('/ttc/api/random-recipe', {
        params: { userId },
      });
      if (!mountedRef.current) return;
      setRecipe(response.data);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('❌ Error fetching recipe:', err);
      setError('Could not fetch recipe. Try again later.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  /** Submit evaluation */
  const evaluateRecipe = useCallback(
    async evalValue => {
      if (!recipe._id || !userdata._id) {
        console.warn('Missing recipe or user ID for evaluation.');
        return;
      }
      try {
        await axiosInstance.post('/ttc/api/evaluate-recipe', {
          userId: userdata._id,
          recipeId: recipe._id,
          evaluation: evalValue,
        });
        setEvaluation(null);
        setActiveButton(null);
        fetchRandomRecipe(userdata._id);
      } catch (err) {
        console.error('❌ Error evaluating recipe:', err);
        setError('Failed to submit evaluation. Please retry.');
      }
    },
    [recipe._id, userdata._id, fetchRandomRecipe]
  );

  /** Lifecycle hooks */
  useEffect(() => {
    mountedRef.current = true;
    getUser();
    return () => {
      mountedRef.current = false;
    };
  }, [getUser]);

  useEffect(() => {
    if (userdata?._id) fetchRandomRecipe(userdata._id);
  }, [userdata, fetchRandomRecipe]);

  /** Loading and error states */
  if (loading) return <div>Loading dashboard…</div>;
  if (error)
    return (
      <div>
        Error: {error}{' '}
        <button type="button" onClick={getUser}>
          Retry
        </button>
      </div>
    );

  /** Format recipe details */
  const ingredients = recipe.ingredients?.split('|').map(i => i.trim()) || [];
  const instructions =
    recipe.instructions
      ?.split('.')
      .map(i => i.trim())
      .filter(Boolean) || [];

  return (
    <div id="outer">
      <div id="inner_remaining">
        <div className="content">
          <div className="recp_name">
            <b>
              <p>🍽️ {recipe.title}</p>
              <p>
                <i>Ingredients</i>
              </p>
            </b>
            <div className="ingredients-container">
              <table id="ing1">
                <tbody>
                  {ingredients.map(
                    (ingredient, index) =>
                      index % 3 === 0 && (
                        <tr key={index}>
                          {ingredients
                            .slice(index, index + 3)
                            .map((ing, i) => (
                              <td key={i}>{ing}</td>
                            ))}
                        </tr>
                      )
                  )}
                </tbody>
              </table>
              <ul id="ing2">
                {ingredients.map((ingredient, index) => (
                  <li key={index} style={{ listStyle: 'none', padding: 0 }}>
                    ✅ {ingredient}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="instructions">
          <b>
            <i>Instructions</i>
          </b>
          <ul>
            {instructions.map((instruction, index) => (
              <li key={index}>
                <b>🥣 {instruction.charAt(0).toUpperCase() + instruction.slice(1)}</b>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bottom_box" id="inner_fixed">
        <div className="evaluate">
          <div className="evaluate_upper">
            <h1>Is this recipe fake or real?</h1>
            <ul className="progressbar">
              {[0, 1, 2].map(i => (
                <li
                  key={i}
                  id={`bt${i}`}
                  className={`blurry ${activeButton === i ? 'active' : ''}`}
                  onClick={() => {
                    setEvaluation('fake');
                    setActiveButton(i);
                  }}
                  aria-label={`Fake option ${i}`}
                />
              ))}
              {[3, 4, 5].map(i => (
                <li
                  key={i}
                  id={`bt${i}`}
                  className={`blurry ${activeButton === i ? 'active' : ''}`}
                  onClick={() => {
                    setEvaluation('real');
                    setActiveButton(i);
                  }}
                  aria-label={`Real option ${i}`}
                />
              ))}
            </ul>
            <ul className="bts">
              <li>
                <button
                  className="sbtn"
                  id="sbt"
                  type="button"
                  onClick={() => evaluation && evaluateRecipe(evaluation)}
                  disabled={!evaluation}
                >
                  Submit
                </button>
              </li>
              <li>
                <button
                  className="sbtn"
                  id="skp"
                  type="button"
                  onClick={() => evaluateRecipe('skip')}
                >
                  Skip
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomRecipeComponent;
